import React from "react";
import ResearchArticle, {
  Body,
  Figure,
  List,
  Notes,
  Points,
  Section,
  Standout,
  Timeline,
} from "../components/portfolio/ResearchArticle";
import { relatedArticles } from "../data/articles";
import { DESIGN_SYSTEM_ANIMATION } from "../data/portfolioWork";

// Design system — a payments company of about 400 people. A short read meant
// to earn a conversation: the problem, how a system one designer started
// became the company's default way of building frontend (the part told in
// depth), what made AI output dependable, and the result. Architecture
// detail is for the call, not the page.
//
// Company, system name, collaborators, merchants and package paths stay
// anonymous: the system is never named in copy, only where its anonymised
// name shows in a captured screen. Two speed claims must stay apart: the
// 20 components in 2 days were a prototype-quality test; the 3–4 days against
// a 2-week estimate were a frontend developer's production work on a real
// project, built through the workflow.

const META = [
  { label: "Role", value: "Sole designer of the system and its design-to-code workflow" },
  { label: "Team", value: "Me, then a staff React engineer for the production package" },
  { label: "When", value: "First version Aug–Sep 2025, in use since" },
  { label: "Company", value: "Payments company, about 400 people" },
];

const OUTCOMES = [
  { value: "200+", label: "Screens built across six product projects" },
  { value: "3–4 days", label: "Frontend work estimated at 2 weeks" },
  { value: "52", label: "Components published" },
];

const ADOPTION = [
  {
    when: "Aug–Sep 2025",
    title: "A first version, alone",
    copy: "Foundations and token architecture, 40 components in Figma, and a design-to-code workflow with a Figma plugin that exports the tokens.",
  },
  {
    title: "A demo to department heads",
    copy: "A couple of components taken through the workflow into code.",
  },
  {
    title: "20 components in two days",
    copy: "Built with AI in a React prototype to test how far it could go. Roughly 90% of behaviour and visual fidelity landed on the first pass.",
  },
  {
    title: "The CTO, then the company",
    copy: "There was little resistance — every team wanted one standard library — and the company adopted the system.",
  },
  {
    title: "A staff React engineer joins",
    copy: "To build the production package from my Figma components and workflow.",
  },
  {
    when: "Today",
    title: "The default way of building frontend",
    copy: "Six product projects and more than 200 screens built from it.",
  },
];

const LAYERS = [
  {
    title: "Tokens",
    copy: "Primitive, semantic and component values, generated from Figma for web, iOS and Android.",
  },
  {
    title: "Screen recipes",
    copy: "A machine-readable layer on each Figma frame that names every component, its order and its props.",
  },
  {
    title: "Skills",
    copy: "Instructions that tell an assistant how to build a screen, and when to stop for review.",
  },
];

const PROOF = [
  "Adoption followed a demo people could see working in code.",
  "A shared library was also an argument about moving developers between teams.",
  "Deterministic recipes and skills, not better prompts, made AI output dependable.",
  "Speed came from removing translation, not from removing review.",
];

const NOTES = [
  "The company and the system are anonymised. The screens are the system’s own site, with its name and content changed.",
  "The 20 components built in two days were a prototype-quality test, not the production package.",
];

function CaseStudyDesignSystem() {
  return (
    <ResearchArticle
      eyebrow="Case study"
      title="The design system people and AI build every product from"
      lead="Every team built interfaces its own way, so design intent was reinterpreted on its way to code — by people, and then by AI. I designed one foundation that people and AI build from, built its first version alone in two months, and it became the company’s default way of building frontend."
      outcomes={OUTCOMES}
      meta={META}
      hero={{
        type: "video",
        src: DESIGN_SYSTEM_ANIMATION,
        frame: "browser",
        alt: "The system’s home page opening: the headline Turn product intent into working interfaces fades in, then cursors for a product manager, a product owner, a designer, a developer and an AI agent arrive around it",
        label: "From intent to interface",
        caption: "One place to start from, whoever is building the screen.",
      }}
      related={relatedArticles("/case-study/design-system")}
    >
      <Section heading="Every team built UI its own way" tocLabel="The problem">
        <Body>
          Figma files ran on local components, two UI libraries overlapped with
          per-project custom work, and local CSS filled the rest. Moving a
          developer to another team meant learning another library first, and
          AI, without structure, guessed. When a high-volume checkout redesign
          exposed the gap, I proposed a parallel path: deliver the checkout,
          and build the shared system it should have been built with.
        </Body>
      </Section>

      <Section heading="From a first version to the company default" tocLabel="Adoption">
        <Timeline items={ADOPTION} />
        <Body>
          From there the split was clear. I owned system direction, the token
          model, component design, the Figma source of truth and the
          design-to-code workflow. He owned React architecture, package
          quality, accessibility and reliability. Component APIs we decided
          together, so a component means the same thing in Figma, in code and
          in its docs.
        </Body>
        <Figure
          type="video"
          src="/videos/ds-foundation"
          frame="browser-dark"
          alt="In dark mode, a product spec, a Figma frame, design tokens, and rules and skills flow along animated lines into one foundation, which renders the same Notifications screen as the preview switches between web, iOS and Android, ready for design and code review. Below it, what the foundation serves: Figma, React, React Native, SwiftUI and AI assistants"
          label="One shared foundation"
          caption="A spec, a Figma frame, tokens and skills go in; the same screen comes out on every platform."
        />
      </Section>

      <Section heading="What made AI output dependable" tocLabel="The system">
        <Body>
          Components alone still left a model guessing layout, order and
          props, so the system carries three more layers. The docs a person
          reads and the context an assistant loads come from the same source.
        </Body>
        <Points items={LAYERS} />
        <Figure
          type="video"
          src="/videos/ds-recipe"
          frame="browser-dark"
          alt="A docs page in dark mode: a Notifications screen switches from a screenshot to its recipe, and pointing at each numbered part highlights the component on screen and the lines of exported JSON that describe it, then the view returns to the screenshot"
          label="Screenshot or recipe"
          caption="A screenshot only shows pixels. The recipe names each part."
        />
        <Standout>
          AI output is stochastic. I made the system around it deterministic.
        </Standout>
        <Body>
          It stays current without relying on memory: skills that connect
          Figma and Jira draft each new component’s docs and write up any drift
          between Figma and code as a ticket, which I review, and the staff
          engineer ships each change as a versioned release.
        </Body>
      </Section>

      <Section heading="Two weeks of frontend work in three to four days" tocLabel="Results">
        <Body>
          On one project, a frontend developer working from the Figma designs
          through the workflow finished frontend work estimated at two weeks in
          three to four days. Developers, designers, product owners prototyping
          and AI assistants all build from the system, alongside 20+ client
          brand themes.
        </Body>
      </Section>

      <Section heading="What this proves" tocLabel="Takeaways">
        <List items={PROOF} />
      </Section>

      <Notes items={NOTES} />
    </ResearchArticle>
  );
}

export default CaseStudyDesignSystem;
